import React from "react";
import {
    makeStyles, withStyles,
    Grid, Typography, Button, Divider,
} from '@material-ui/core';
import {ValidatorForm} from 'react-material-ui-form-validator';
import SaveIcon from '@material-ui/icons/Save';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import Review from './Review'
import EntitlementRecordRules from "../../../../shared/ValidationRules/EntitlementRecordRules";
import TabPanel from './TabPanel';
import Box from "@material-ui/core/Box";
import CustomTextField from "./CustomTextField";
import CustomDateField from "./CustomDateField";
import customAxios from "../../../../API/customAxios";
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex-direction',
        flexWrap: 'wrap',
        height: '600px'
    },
    formSubtitle: {
        fontSize: 14,
        fontWeight: 600,
        color: '#3F51B5',
        paddingBottom: theme.spacing(2),
    },
    saveButtonDiv: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    saveButton: {
        backgroundColor: '#2C3E50',
        '&:hover': {
            backgroundColor: '#080C0F',
        },
    },
}));

export default function EntitlementRecord({showId, employeeName, allowanceRecord, updateAllowanceRecord, backToTable}) {
    const classes = useStyles();
    const form = React.useRef();
    const [open, setOpen] = React.useState(false);
    const [values, setValues] = React.useState(allowanceRecord);

    // Sick Leave Balance Check
    const [balanceCalculationDate, setBalanceCalculationDate] = React.useState(moment().format('YYYY-MM-DD'));
    const [balanceResult, setBalanceResult] = React.useState(null);
    const [isLoadingBalance, setIsLoadingBalance] = React.useState(false);

    React.useEffect(() => {
        if (showId && balanceCalculationDate) {
            fetchSickLeaveBalanceForDate(balanceCalculationDate);
        }
    }, [showId]);

    // Selector change handling
    const handleALChange = (idx, event) => {
        const name = event.target.name;
        let data = {...values};
        data['annual_allowances'][idx][name] = event.target.value;
        setValues(data);
    };

    const handleSLChange = (event) => {
        const name = event.target.name;
        let data = {...values};
        data['sick_allowances'][name] = event.target.value;
        setValues(data);
    };

    const handleBalanceDateChange = (event) => {
        const selectedDate = event.target.value;
        setBalanceCalculationDate(selectedDate);
        setBalanceResult(null);


        if (selectedDate && showId) {
            fetchSickLeaveBalanceForDate(selectedDate);
        }
    };

    const fetchSickLeaveBalanceForDate = async (date) => {
        if (!showId || !date) {
            return;
        }

        setIsLoadingBalance(true);
        try {
            const response = await customAxios.get('/sick_leave_balance_by_date', {
                params: {
                    employee_id: showId,
                    calculation_date: date
                }
            });

            if (response.data.success) {
                setBalanceResult(response.data.data);
            } else {
                console.error('Failed to get sick leave balance: ' + response.data.message);
                setBalanceResult(null);
            }
        } catch (error) {
            console.error('Error fetching sick leave balance:', error);
            setBalanceResult(null);
        } finally {
            setIsLoadingBalance(false);
        }
    };

    const fetchSickLeaveBalance = async () => {
        if (!showId || !balanceCalculationDate) {
            return;
        }

        setIsLoadingBalance(true);
        try {
            const response = await customAxios.get('/sick_leave_balance_by_date', {
                params: {
                    employee_id: showId,
                    calculation_date: balanceCalculationDate
                }
            });

            if (response.data.success) {
                setBalanceResult(response.data.data);
            } else {
                alert('Failed to get sick leave balance: ' + response.data.message);
                setBalanceResult(null);
            }
        } catch (error) {
            console.error('Error fetching sick leave balance:', error);
            alert('Error fetching sick leave balance: ' + error);
            setBalanceResult(null);
        } finally {
            setIsLoadingBalance(false);
        }
    };

    // Application summary pop up handling
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const updateAllowance = () => {
        updateAllowanceRecord(values);
    };

    // Form submission handling
    const handleSubmit = evt => {
        evt.preventDefault();
        const validation = EntitlementRecordRules(values);
        if (validation['isValid'] === false) {
            alert(validation['errorMessage']);
        } else {
            handleOpen();
        }
    };

    return (
        <ValidatorForm
            ref={form}
            onSubmit={handleSubmit}
            onError={errors => (errors)}
        >
            <Grid container spacing={1}>
                <Grid item xs={8}>
                    <Typography variant="h5" component="h2" className={classes.formSubtitle}>
                        Annual Leave <BorderColorIcon style={{fontSize: 12}}/>
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <div className={classes.saveButtonDiv}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<SaveIcon/>}
                            className={classes.saveButton}
                        >
                            Save
                        </Button>
                    </div>
                </Grid>
                <TabPanel annualAllowances={allowanceRecord.annual_allowances} handleALChange={handleALChange}/>
                <Grid item xs={12}>
                    <Typography variant="h5" component="h2" className={classes.formSubtitle}>
                        Sick Leave
                    </Typography>
                    <Grid container spacing={1}>
                        <CustomTextField
                            label={'Entitled'}
                            name={'total'}
                            values={values.sick_allowances.total}
                            handleChange={handleSLChange}
                            type={'number'}
                            validators={['required']}
                            space={6}
                            readonly={true}
                        />
                        <CustomTextField
                            label={'Taken'}
                            name={'taken'}
                            values={values.sick_allowances.taken}
                            handleChange={handleSLChange}
                            type={'number'}
                            validators={['required']}
                            space={6}
                            readonly={true}
                        />
                        <CustomTextField
                            label={'Balance'}
                            name={'balance'}
                            values={Number(values.sick_allowances.total) - Number(values.sick_allowances.taken)}
                            handleChange={handleSLChange}
                            type={'number'}
                            validators={[]}
                            space={6}
                            readonly={true}
                        />
                        <CustomTextField
                            label={'Effective Date'}
                            name={'effective_date'}
                            values={values.sick_allowances.effective_date}
                            handleChange={handleSLChange}
                            type={'string'}
                            validators={[]}
                            space={6}
                            readonly={true}
                        />
                        <CustomTextField
                            label={'Check Balance by Date'}
                            name={'balance_calculation_date'}
                            values={balanceCalculationDate}
                            handleChange={handleBalanceDateChange}
                            type={'date'}
                            validators={[]}
                            space={6}
                        />
                        <CustomTextField
                            label={'Balance at Selected Date'}
                            name={'balance_at_date'}
                            values={balanceResult ? balanceResult.balance : (isLoadingBalance ? 'Checking...' : '')}
                            handleChange={() => {}}
                            type={'string'}
                            validators={[]}
                            space={6}
                            readonly={true}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h5" component="h2" className={classes.formSubtitle}>
                        Other Leave Taken
                    </Typography>
                    <Grid container spacing={1}>
                        {values.allowances_without_total.map((annualAllowance, i) => (
                            <CustomTextField
                                key={i}
                                label={annualAllowance.type.name}
                                name={annualAllowance.type.name}
                                values={annualAllowance.amount}
                                handleChange={() => {
                                    //
                                }}
                                type={'number'}
                                validators={[]}
                                space={6}
                                readonly={true}
                            />
                        ))}
                    </Grid>
                </Grid>
            </Grid>
            {open && (
                <Review
                    handleClose={handleClose}
                    backToTable={backToTable}
                    open={open}
                    showId={showId}
                    employeeName={employeeName}
                    values={values}
                    updateAllowance={updateAllowance}
                />
            )}
        </ValidatorForm>
    );
}
